/**
 * My Account → Moje zwierzaki — Add / Edit / Delete via REST hn/v1/pets.
 *
 * Template: templates/my-account-pets.php
 * REST: inc/pet-profiles.php (GET /pets list, POST /pets save, DELETE /pets/{id})
 * Config localized as `window.hnPets` (restUrl, nonce, labels).
 */

export function initPetProfiles() {
    const container = document.querySelector('[data-hn-pets]');
    if (!container) return;
    if (typeof window.hnPets === 'undefined') return;

    const cfg = window.hnPets;
    const modal = container.querySelector('[data-hn-pet-modal]');
    const form = container.querySelector('[data-hn-pet-form]');
    const formTitle = container.querySelector('[data-hn-pet-form-title]');
    const errorEl = container.querySelector('[data-hn-pet-error]');
    const cancelBtn = container.querySelector('[data-hn-pet-cancel]');
    const addBtn = container.querySelector('[data-hn-pet-add]');
    if (!modal || !form) return;

    // ---- helpers --------------------------------------------------------

    function openModal() {
        if (typeof modal.showModal === 'function') modal.showModal();
        else modal.setAttribute('open', '');
    }
    function closeModal() {
        if (typeof modal.close === 'function') modal.close();
        else modal.removeAttribute('open');
    }
    function showError(msg) {
        if (!errorEl) return;
        errorEl.textContent = msg || cfg.labels?.error || 'Błąd';
        errorEl.hidden = false;
    }
    function clearError() {
        if (!errorEl) return;
        errorEl.textContent = '';
        errorEl.hidden = true;
    }
    function resetForm() {
        form.reset();
        const idInput = form.querySelector('input[name="id"]');
        if (idInput) idInput.value = '';
        clearError();
    }
    function fillForm(pet) {
        resetForm();
        const set = (name, val) => {
            const el = form.querySelector(`[name="${name}"]`);
            if (!el) return;
            if (el.type === 'checkbox') el.checked = !!val;
            else el.value = val == null ? '' : val;
        };
        set('id', pet.id);
        set('name', pet.name);
        set('species', pet.species || 'dog');
        set('breed', pet.breed || '');
        set('birthdate', pet.birthdate || '');
        set('weight_kg', pet.weight_kg || '');
        set('sex', pet.sex || '');
        set('neutered', pet.neutered);
        set('activity', pet.activity || 'normal');
        set('body_condition', pet.body_condition || 'ideal');
        set('notes', pet.notes || '');

        const allergySlugs = Array.isArray(pet.allergies)
            ? pet.allergies
            : (pet.allergies_pretty || []).map(a => a.slug);
        form.querySelectorAll('input[name="allergies[]"]').forEach(cb => {
            cb.checked = allergySlugs.includes(cb.value);
        });
    }

    async function rest(method, url, body) {
        const opts = {
            method,
            credentials: 'same-origin',
            headers: { 'X-WP-Nonce': cfg.nonce },
        };
        if (body !== undefined) {
            opts.headers['Content-Type'] = 'application/json';
            opts.body = JSON.stringify(body);
        }
        const res = await fetch(url, opts);
        let data = null;
        try { data = await res.json(); } catch (_) {}
        if (!res.ok) {
            const msg = (data && (data.message || data.code)) || `HTTP ${res.status}`;
            throw new Error(msg);
        }
        return data;
    }

    let cachedPets = null;
    async function getPets(force = false) {
        if (!force && cachedPets) return cachedPets;
        cachedPets = await rest('GET', cfg.restUrl);
        return cachedPets;
    }

    // ---- handlers -------------------------------------------------------

    if (addBtn) {
        addBtn.addEventListener('click', () => {
            resetForm();
            if (formTitle) formTitle.textContent = 'Dodaj zwierzaka';
            openModal();
        });
    }

    if (cancelBtn) {
        cancelBtn.addEventListener('click', () => closeModal());
    }

    // Delegated — działa po re-render listy.
    container.addEventListener('click', async (e) => {
        const editBtn = e.target.closest('[data-hn-pet-edit]');
        const delBtn = e.target.closest('[data-hn-pet-delete]');
        if (!editBtn && !delBtn) return;

        const card = e.target.closest('[data-pet-id]');
        if (!card) return;
        const petId = parseInt(card.getAttribute('data-pet-id'), 10);
        if (!petId) return;

        if (editBtn) {
            try {
                const pets = await getPets();
                const pet = pets.find(p => parseInt(p.id, 10) === petId);
                if (!pet) {
                    cachedPets = null;
                    alert('Nie udało się załadować profilu — odśwież stronę.');
                    return;
                }
                fillForm(pet);
                if (formTitle) formTitle.textContent = 'Edytuj zwierzaka';
                openModal();
            } catch (err) {
                alert('Błąd: ' + err.message);
            }
            return;
        }

        if (delBtn) {
            const confirmMsg = cfg.labels?.confirmDelete || 'Usunąć profil zwierzaka?';
            if (!confirm(confirmMsg)) return;
            try {
                await rest('DELETE', `${cfg.restUrl}/${petId}`);
                cachedPets = null;
                card.remove();
                if (!container.querySelector('.hn-pet-card')) {
                    window.location.reload();
                }
            } catch (err) {
                alert('Błąd: ' + err.message);
            }
        }
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        clearError();

        const fd = new FormData(form);
        const payload = {
            id: parseInt(fd.get('id') || '0', 10) || undefined,
            name: (fd.get('name') || '').toString().trim(),
            species: fd.get('species') || 'dog',
            breed: (fd.get('breed') || '').toString().trim(),
            birthdate: fd.get('birthdate') || '',
            weight_kg: parseFloat(fd.get('weight_kg')) || 0,
            sex: fd.get('sex') || '',
            neutered: form.querySelector('[name="neutered"]')?.checked ? 1 : 0,
            activity: fd.get('activity') || 'normal',
            body_condition: fd.get('body_condition') || 'ideal',
            notes: (fd.get('notes') || '').toString(),
            allergies: fd.getAll('allergies[]').filter(Boolean),
        };

        if (!payload.name) {
            showError('Imię jest wymagane');
            return;
        }

        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;
        try {
            await rest('POST', cfg.restUrl, payload);
            cachedPets = null;
            closeModal();
            // Najprościej — reload, żeby PHP zrenderował świeży card z poprawnym HTML
            // (avatar, allergy labels, sformatowany age string).
            window.location.reload();
        } catch (err) {
            showError(err.message);
        } finally {
            if (submitBtn) submitBtn.disabled = false;
        }
    });
}
